import {
	Badge,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect, useState } from "react"
import { TAutomationScheduledRun, XHR } from "./store"
import { ScheduleTraceDialog } from "./ScheduleTraceDialog"
import { FormatRunContext } from "./Runs"
import { SelectInputField } from "@sembark-travel/ui-form"

function scheduleMode(run: TAutomationScheduledRun) {
	return (
		run.mode ||
		(typeof run.payload?.mode === "string" ? run.payload.mode : null)
	)
}

function scheduleOccurrenceIndex(run: TAutomationScheduledRun) {
	return typeof run.occurrence_index === "number"
		? run.occurrence_index
		: typeof run.payload?.occurrence_index === "number"
			? run.payload.occurrence_index
			: null
}

type TFilters = TSearchParams & {
	horizon?: "upcoming" | "overdue" | "all"
	automations?: string
}

export function AutomationSchedulesList({
	automationIds,
	title = "Automation Schedules",
	useUrlState = true,
}: {
	automationIds?: Array<number>
	title?: string
	useUrlState?: boolean
} = {}) {
	const [query, setQuery] = useLocationQuery<TFilters, TFilters>()
	const [urlParams, setUrlParams] = useSearch<TFilters>({
		horizon: "upcoming",
		...query,
	})
	const [localParams, setLocalParams] = useState<Record<string, unknown>>({})

	const params = useUrlState ? urlParams : localParams
	const setParams = useUrlState ? setUrlParams : setLocalParams

	useEffect(() => {
		if (!useUrlState) {
			return
		}
		setQuery(params)
	}, [params, setQuery, useUrlState])

	return (
		<Search
			title={title}
			initialParams={params}
			onSearch={(nextParams) => setParams({ ...nextParams, page: 1 })}
			resetParams={{ q: "", page: 1, horizon: "upcoming" }}
			areAdvancedFiltersApplied={(params) => {
				const { horizon, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={() => (
				<Inline gap="4" flexWrap="wrap" alignItems="center">
					<SelectInputField name="horizon">
						<option value="upcoming">Upcoming</option>
						<option value="overdue">Overdue</option>
						<option value="all">All</option>
					</SelectInputField>
				</Inline>
			)}
		>
			<ListView<TAutomationScheduledRun, TFilters>
				pageKey={"automation-schedules" + (automationIds || []).join("|")}
				params={params}
				onPageChange={(page) =>
					setParams({
						...params,

						page,
					})
				}
				fetch={(xhr, listParams) =>
					XHR(xhr).getSchedules({
						...listParams,
						...(automationIds?.length
							? {
									automations: automationIds.join(","),
								}
							: {}),
					})
				}
			>
				{({ items }) => (
					<Table
						bordered
						hover
						responsive
						headers={[
							"Automation",
							"Context",
							"Checkpoint",
							"Run",
							"Status",
							"Scheduled For",
							"",
						]}
						alignCols={{ 6: "right" }}
						rows={items.map((run) => [
							<Stack gap="1" key={run.id}>
								<Text fontWeight="semibold">
									{run.automation?.source?.name ||
										run.automation?.source_key ||
										"Automation"}
								</Text>
								<Text color="muted" fontSize="sm">
									Schedule #{run.id}
								</Text>
							</Stack>,
							<FormatRunContext run={run} />,
							<Stack gap="1">
								<Text>{run.checkpoint_key}</Text>
								{scheduleMode(run) ? (
									<Text color="muted" fontSize="sm">
										Mode: {scheduleMode(run)}
									</Text>
								) : null}
								{typeof scheduleOccurrenceIndex(run) === "number" ? (
									<Text color="muted" fontSize="sm">
										Occurrence {String(scheduleOccurrenceIndex(run))}
									</Text>
								) : null}
							</Stack>,
							<Stack gap="2">
								{run.automation_run ? (
									<>
										<Text fontSize="sm">Run #{run.automation_run.id}</Text>
										<Stack gap="1">
											<Badge
												warning={run.automation_run.run_mode === "dry_run"}
											>
												{run.automation_run.run_mode === "dry_run"
													? "Dry Run"
													: "Live"}
											</Badge>
											<Badge
												success={run.automation_run.status === "completed"}
												warning={
													run.automation_run.status === "pending" ||
													run.automation_run.status === "running"
												}
												danger={
													run.automation_run.status === "failed" ||
													run.automation_run.status === "cancelled"
												}
												title={run.automation_run.error || undefined}
											>
												{run.automation_run.status}
											</Badge>
										</Stack>
									</>
								) : (
									<Text color="muted">No run yet</Text>
								)}
							</Stack>,
							<Badge
								success={run.status === "completed"}
								warning={
									run.status === "pending" || run.status === "processing"
								}
								danger={run.status === "failed" || run.status === "cancelled"}
								title={run.error || undefined}
							>
								{run.status}
							</Badge>,
							<Stack gap="1">
								<RelativeTime timestamp={run.scheduled_for} />
								{run.processed_at ? (
									<Text color="muted" fontSize="sm">
										Processed <RelativeTime timestamp={run.processed_at} />
									</Text>
								) : null}
							</Stack>,
							<ScheduleTraceDialog scheduledRun={run} />,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}
